import { useApolloClient } from "@apollo/client";
import { Button, Text, TextInput } from "north.js";
import React, { useRef, useState } from "react";
import Modal from "react-modal";
import styled from "styled-components";

import { FastQrReader, Icon } from "src/shared/components";
import { EVENT_ID } from "src/shared/constants/event";
import { ViewFinder } from "src/static/img";

import { TVolunteer } from "../constants";
import {
  FindVolunteerByEmailDocument,
  FindVolunteerByEmailQuery,
  FindVolunteerByEmailQueryVariables,
} from "../graphql/findVolunteerByEmail.generated";

interface QRScannerProps {
  showCameraModal: boolean;
  setShowCameraModal: (showCameraModal: boolean) => void;
  manualInput: boolean;
  setManualInput: (manualInput: boolean) => void;
  onVolunteerFound: (volunteer: TVolunteer) => void;
  onScanError: (message: string) => void;
}

const ViewFinderIndicator = styled(ViewFinder)`
  top: 0px;
  left: 0px;
  z-index: 100;
  box-sizing: border-box;
  position: absolute;
  width: 100%;
  height: 100%;
  padding: 180px;
  @media screen and (max-width: 1280px) {
    padding: 150px;
  }
  @media screen and (max-width: 768px) {
    padding: 100px;
  }
  margin-top: -50px;
`;

const QRScanner: React.FC<QRScannerProps> = ({
  showCameraModal,
  setShowCameraModal,
  manualInput,
  setManualInput,
  onVolunteerFound,
  onScanError,
}) => {
  const [volunteerEmail, setVolunteerEmail] = useState<string>("");
  const [isLookingUp, setIsLookingUp] = useState(false);
  const scanInProgressRef = useRef(false);
  const lookupIdRef = useRef(0);
  const client = useApolloClient();

  const closeScanner = () => {
    lookupIdRef.current += 1;
    setShowCameraModal(false);
    setManualInput(false);
    setVolunteerEmail("");
    setIsLookingUp(false);
    scanInProgressRef.current = false;
  };

  const cancelManualInput = () => {
    lookupIdRef.current += 1;
    setManualInput(false);
    setVolunteerEmail("");
    setIsLookingUp(false);
    scanInProgressRef.current = false;
  };

  const queryVolunteer = (email: string) =>
    client.query<FindVolunteerByEmailQuery, FindVolunteerByEmailQueryVariables>(
      {
        query: FindVolunteerByEmailDocument,
        variables: { email, event_id: EVENT_ID },
        fetchPolicy: "network-only",
      }
    );

  const lookUpVolunteer = async (rawEmail: string) => {
    if (scanInProgressRef.current) return;

    const email = rawEmail.trim();
    if (!email) {
      onScanError("Enter an email address first.");
      return;
    }

    scanInProgressRef.current = true;
    setIsLookingUp(true);
    const lookupId = ++lookupIdRef.current;

    try {
      let { data } = await queryVolunteer(email);
      if (lookupId !== lookupIdRef.current) return;

      // HAPI matches the email exactly, so a phone auto-capitalizing the first
      // letter is enough to miss a volunteer that is really there.
      const lowercased = email.toLowerCase();
      if (!data?.volunteer?.length && lowercased !== email) {
        ({ data } = await queryVolunteer(lowercased));
        if (lookupId !== lookupIdRef.current) return;
      }

      const volunteer = data?.volunteer?.[0];
      if (volunteer) {
        onVolunteerFound({
          eventAttendeeId: volunteer.event_attendee_id,
          name: volunteer.event_attendee.user.name,
          legalName: volunteer.event_attendee.user.legal_name ?? "",
          email: volunteer.event_attendee.user.email,
          primaryRole: volunteer.primary_category_name,
          notes: volunteer.notes,
        });
      } else {
        onScanError(`No volunteer found for ${email}.`);
      }
    } catch (error) {
      if (lookupId !== lookupIdRef.current) return;

      onScanError(
        error instanceof Error
          ? error.message
          : "Could not look that volunteer up. Try again."
      );
    } finally {
      if (lookupId === lookupIdRef.current) {
        closeScanner();
      }
    }
  };

  const handleManualSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    lookUpVolunteer(volunteerEmail);
  };

  return (
    <CameraModal
      isOpen={showCameraModal}
      contentLabel="Scan a volunteer's ticket"
      onRequestClose={closeScanner}
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          zIndex: 100,
        },
      }}
    >
      <VideoHeader manualInput={manualInput}>
        <button onClick={closeScanner}>
          <Icon
            name="chevron-left"
            size="title"
            color={manualInput ? "primary" : "white"}
          />
        </button>
        <div>
          <CheckInInfo>
            <Text
              type="body1"
              style={{
                color: manualInput ? "" : "white",
              }}
            >
              Scan a volunteer&apos;s ticket
            </Text>
            <Text
              style={{
                color: manualInput ? "" : "white",
              }}
            >
              Their info and shifts will show up next
            </Text>
          </CheckInInfo>
        </div>
      </VideoHeader>
      <VideoWrapper>
        {manualInput ? (
          <ManualInputForm onSubmit={handleManualSubmit}>
            <Text>Enter the volunteer&apos;s email address:</Text>
            <BadgeInput
              size="md"
              type="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="email-goes-here"
              value={volunteerEmail}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setVolunteerEmail(e.target.value)
              }
            />
            <ManualInputButtons>
              <Button
                type="button"
                color="secondary"
                size="md"
                onClick={cancelManualInput}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                size="md"
                disabled={isLookingUp || !volunteerEmail.trim()}
              >
                {isLookingUp ? "Looking up..." : "Confirm"}
              </Button>
            </ManualInputButtons>
          </ManualInputForm>
        ) : (
          <>
            <FastQrReader
              onResult={(result) => {
                if (result && !scanInProgressRef.current) {
                  lookUpVolunteer(result.getText());
                }
              }}
              containerStyle={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
              videoContainerStyle={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                padding: "0",
              }}
              videoStyle={{
                width: "100%",
                objectFit: "cover",
                height: "100%",
                position: "relative",
              }}
              ViewFinder={ViewFinderIndicator}
            />
            <ManualButton
              color="primary"
              size="md"
              onClick={() => {
                setManualInput(true);
              }}
            >
              Manually input email
            </ManualButton>
          </>
        )}
      </VideoWrapper>
    </CameraModal>
  );
};

const CameraModal = styled(Modal)`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  background: white;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
`;

const VideoHeader = styled.div<{ manualInput: boolean }>`
  width: 100%;
  display: flex;
  background: #1f2937;
  ${({ manualInput }) =>
    manualInput ? `background: white;` : `background: #1f2937;`}
  padding: 29px 25px;
  align-items: center;
  div {
    display: flex;
    flex-direction: column;
    padding-left: 12px;
  }
`;

const VideoWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const ManualInputForm = styled.form`
  margin: 7vh auto 0;
  display: flex;
  flex-direction: column;
  padding: 0 30px;
  width: 100%;
  max-width: 420px;
`;

const BadgeInput = styled(TextInput)`
  width: 100%;
  margin-top: 16px;
`;

const ManualInputButtons = styled.div`
  margin-top: 16px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  button {
    width: 100%;
  }
`;

const ManualButton = styled(Button)`
  --buttonWidth: 250px;
  position: fixed;
  width: var(--buttonWidth) !important;
  bottom: 78px;
  left: calc(50vw - var(--buttonWidth) / 2);
  z-index: 10000;
  height: 42px !important;
  * button {
    color: #43afde !important;
  }
`;

const CheckInInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: start;
`;

export default QRScanner;
